import React, { useEffect, useState } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { BAILLEUR_LINKS } from './links';
import { bailleurApi, downloadPdfWithAuth } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { TID } from '@/constants/testIds';
import { Plus, Wallet, TrendingDown, TrendingUp, ReceiptText, Download, Users } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

function KpiTile({ label, amount, currency = 'USD', tone = 'default', icon: Icon, testid }) {
  const tones = {
    default: 'bg-white border-slate-200',
    dark: 'bg-[#0D1B2A] text-white border-[#0D1B2A]',
    accent: 'bg-[#E6F1FF] border-[#BEDBFF]',
    danger: 'bg-red-50 border-red-200',
  };
  const isDark = tone === 'dark';
  return (
    <div data-testid={testid} className={`rounded-xl border p-5 ${tones[tone]}`}>
      <div className="flex items-start justify-between">
        <div className={`text-[11px] font-semibold uppercase tracking-widest ${isDark ? 'text-white/70' : 'text-slate-500'}`}>{label}</div>
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
      </div>
      <div className="mt-3 text-3xl font-bold tracking-tight">
        {typeof amount === 'number' ? amount.toLocaleString(undefined, { maximumFractionDigits: 2 }) : amount}
        {currency && <span className="text-sm font-medium ml-1 opacity-70">{currency}</span>}
      </div>
    </div>
  );
}

export default function BailleurDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    let mounted = true;
    const load = async (silent = false) => {
      const el = document.activeElement;
      if (silent && el && ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return;
      if (silent && document.visibilityState === 'hidden') return;
      try {
        const { data: next } = await bailleurApi.dashboard();
        if (mounted) setData(next);
      } catch (error) {
        if (!silent && error?.response?.status === 402) nav('/bailleur/abonnement', { replace: true });
      } finally {
        if (!silent) setLoading(false);
      }
    };
    load(false);
    const timer = setInterval(() => load(true), 30_000);
    return () => { mounted = false; clearInterval(timer); };
  }, [nav]);

  const openReceipt = async (payment) => {
    try {
      await downloadPdfWithAuth(bailleurApi.recuUrl(payment.id), `${payment.recu_numero}.pdf`);
    } catch {
      toast.error('Téléchargement impossible');
    }
  };

  if (loading || !data) return <AppLayout links={BAILLEUR_LINKS} testid={TID.bailleurSidebar} title="Tableau de bord" />;

  return (
    <AppLayout links={BAILLEUR_LINKS} testid={TID.bailleurSidebar} title={`Bonjour, ${data.bailleur.prenom}`}>
      <p className="-mt-4 mb-6 text-sm text-slate-500">Voici un aperçu de vos loyers et locataires.</p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile testid={TID.bailleurKpiAttendu} label="Attendu ce mois" tone="dark" amount={data.kpis.loyer_attendu_usd} icon={Wallet} />
        <KpiTile testid={TID.bailleurKpiRecu} label="Reçu ce mois" tone="accent" amount={data.kpis.loyer_recu_usd} icon={TrendingUp} />
        <KpiTile testid={TID.bailleurKpiRetard} label="En retard" tone="danger" amount={data.kpis.loyer_retard_usd} icon={TrendingDown} />
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={TID.bailleurQuota}>
          <div className="flex items-start justify-between"><div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Locataires</div><Users className="h-4 w-4 text-emerald-500" /></div>
          <div className="mt-3 text-3xl font-bold text-[#0D1B2A]">{data.quota.used}</div>
          <div className="mt-1 text-xs font-semibold text-emerald-600">Illimités avec LoyerBook</div>
        </div>
      </div>

      <div className="mt-8 grid gap-4 lg:grid-cols-3">
        <div className="lb-card p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <div><div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Derniers paiements</div><div className="mt-1 text-lg font-bold text-[#0D1B2A]">Historique récent</div></div>
            <Button variant="outline" onClick={() => nav('/bailleur/paiements')}>Voir tout</Button>
          </div>
          {data.last_payments.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500">Aucun paiement enregistré.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.last_payments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between py-3">
                  <div><div className="text-sm font-semibold text-[#0D1B2A]">{payment.locataire ? `${payment.locataire.prenom} ${payment.locataire.nom}` : 'Locataire'}</div><div className="text-xs text-slate-500">{payment.recu_periode} · {new Date(payment.date_paiement).toLocaleDateString('fr-FR')}</div></div>
                  <div className="flex items-center gap-3"><div className="text-right"><div className="font-bold text-[#0D1B2A]">{payment.montant} {payment.devise}</div><div className="text-[10px] uppercase text-slate-500">{payment.mode}</div></div><Button size="sm" variant="ghost" onClick={() => openReceipt(payment)}><Download className="h-4 w-4" /></Button></div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lb-card p-6">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Actions</div>
          <div className="mt-4 space-y-3">
            <Link to="/bailleur/locataires" className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 transition-colors hover:border-[#1677FF] hover:bg-[#E6F1FF]/50"><div><div className="text-sm font-semibold text-[#0D1B2A]">Ajouter un locataire</div><div className="text-xs text-slate-500">Aucune limite de locataires</div></div><Plus className="h-4 w-4 text-[#1677FF]" /></Link>
            <Link to="/bailleur/paiements" className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 transition-colors hover:border-[#1677FF] hover:bg-[#E6F1FF]/50"><div><div className="text-sm font-semibold text-[#0D1B2A]">Enregistrer un paiement</div><div className="text-xs text-slate-500">Générer un reçu PDF</div></div><ReceiptText className="h-4 w-4 text-[#1677FF]" /></Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
